import { Resource } from 'src_common/common/mobx-utils/Resource';
import { observable, computed, action, makeObservable, runInAction } from 'mobx';
import { UserLoginState } from './UserLoginState';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { OpenapiProxyStaffAccountAffordabilityFetchAllResponse200Type as ResponseType } from 'src/api_openapi/generated/openapi_proxy_staff_account_affordability_fetch_all';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { TrpcClient } from 'src/AppState/TrpcClient';

type ResponseDataInferredType = ResponseType['data'] extends Array<infer T> ? T : never;

export type ViewType = 'Red' | 'Amber' | 'Green';

export type ViewArrayType = 'Red' | 'Amber' | 'Green' | Array<'Red' | 'Amber' | 'Green'>;

const getResource = (
    sdkStaff: SdkStaff,
    type: ViewArrayType,
    page: number,
    perPage: number
): Resource<ResponseType> => {
    return new Resource(async (): Promise<ResponseType> => {
        const colorList: string[] = Array.isArray(type) ? [...type] : [type];

        const response = await sdkStaff.postAffordabilityFetchAll({
            requestBody: {
                page,
                perPage,
                colorFilterList: colorList, //['Amber', 'Red', 'Green'],
                triggerFilterList: ['Auto', 'Trigger', 'Manual'],
            },
        });

        response.data.sort((a, b) => {
            if (a.lastCheckDate > b.lastCheckDate) {
                return -1;
            }
            if (a.lastCheckDate < b.lastCheckDate) {
                return 1;
            }
            return 0;
        });

        return response;
    });
};

export class AffordabilityExcludedState {
    private readonly data: AutoMap<ViewArrayType, AutoMap<number, Resource<ResponseType>>>;
    public readonly perPage: number;
    @observable private totalPages: number;
    @observable public page: number;
    @observable public affordabilityStatus: ViewArrayType;
    @observable public statusDropdown: boolean;
    @observable public actionDropdown: boolean;
    @observable public alertBox: boolean;
    @observable public accountId: number | null;
    @observable public dangerCheckbox: boolean;
    @observable private customerNames: Map<number, { firstName: string; surname: string }> = new Map();

    private readonly fetchedAccountIds: Set<number> = new Set();

    public constructor(
        public readonly userLoginState: UserLoginState,
        private readonly sdkStaff: SdkStaff,
        private readonly trpcClient: TrpcClient
    ) {
        this.totalPages = 0;
        this.perPage = 10;
        this.page = 0;
        this.affordabilityStatus = ['Red', 'Amber', 'Green'];
        this.data = new AutoMap(
            (type: ViewArrayType) => new AutoMap((page: number) => getResource(this.sdkStaff, type, page, this.perPage))
        );
        this.statusDropdown = false;
        this.actionDropdown = false;
        this.alertBox = false;
        this.accountId = null;
        this.dangerCheckbox = false;
        makeObservable(this);
    }

    @computed private get list(): Array<ResponseDataInferredType> | null {
        return this.totalItems?.data ?? null;
    }

    @computed public get activeTab(): ViewType {
        return Array.isArray(this.affordabilityStatus) ? 'Red' : this.affordabilityStatus;
    }

    @computed public get allList(): Array<ResponseDataInferredType> {
        return this.list ?? [];
    }

    public ensureCustomerNames = (accountIds: number[]): void => {
        const newAccountIds = accountIds.filter((accountId) => this.fetchedAccountIds.has(accountId) === false);

        for (const accountId of newAccountIds) {
            this.fetchedAccountIds.add(accountId);
        }

        if (newAccountIds.length > 0) {
            void this.fetchCustomerNames(newAccountIds);
        }
    };

    public getCustomerName = (accountId: number): string => {
        const customer = this.customerNames.get(accountId);
        if (customer === undefined) {
            return '-';
        }

        const name = `${customer.firstName} ${customer.surname}`.trim();
        return name === '' ? '-' : name;
    };

    @action private fetchCustomerNames = async (accountIds: number[]): Promise<void> => {
        try {
            const response = await this.trpcClient.client.accountData.searchAccountListByIds.mutate({
                ids: accountIds,
                include: ['id', 'properties.firstName', 'properties.surname'],
            });

            if (response.responseStatus === 'success') {
                runInAction(() => {
                    response.data.results.forEach((item) => {
                        this.customerNames.set(item.id, {
                            firstName: item.properties.firstName ?? '',
                            surname: item.properties.surname ?? '',
                        });
                    });
                });
            }
        } catch (error) {
            console.error(error);
        }
    };

    @computed public get totalItems(): ResponseType | null {
        const totalItems = this.data.get(this.affordabilityStatus).get(this.page);
        const data = totalItems.get();

        if (data.type === 'ready') {
            return data.value;
        }

        return null;
    }

    public getAllTabs = (): Array<'Red' | 'Amber' | 'Green'> => {
        return ['Red', 'Amber', 'Green'];
    };

    @computed public get totalItemsLength(): number {
        if (this.totalItems === null) {
            return this.totalPages;
        }

        this.totalPages = this.totalItems.total;

        return this.totalPages;
    }

    @action public handleMove = (move: boolean): void => {
        if (move === true && this.page > 0) {
            this.page = this.page - 1;
        } else if (move === false && this.page < Math.ceil(this.totalItemsLength / this.perPage) - 1) {
            this.page = this.page + 1;
        }
    };

    @action public handleChoosePage = (index: number): void => {
        this.page = index;
    };

    @computed public get pageNumbers(): number[] {
        const pageNumbersResult = [];

        let startPage = 0;
        let endPage = 5;

        if (this.page >= 5) {
            startPage = this.page - 4;
        }

        if (this.page <= Math.ceil(this.totalItemsLength / this.perPage) - 5) {
            endPage = this.page + 5;
        } else {
            endPage = this.page + (Math.ceil(this.totalItemsLength / this.perPage) - this.page);
        }

        for (let i = startPage; i < endPage; i++) {
            pageNumbersResult.push(i);
        }

        return pageNumbersResult;
    }

    @action public handleChangeNav = (tabName: ViewType): void => {
        this.affordabilityStatus = tabName;
        this.page = 0;
    };

    @action public setAlertBox = (): void => {
        this.alertBox = !this.alertBox;
        this.dangerCheckbox = false;
    };

    @action public handleStatus = (clickOutside: boolean): void => {
        if (clickOutside === true) {
            this.statusDropdown = false;
        }
    };

    @action public handleClickStatus = (): void => {
        this.statusDropdown = !this.statusDropdown;
    };

    @action public handleAction = (clickOutside: boolean): void => {
        if (clickOutside === true) {
            this.actionDropdown = false;
        }
    };

    @action public handleClickAction = (): void => {
        this.actionDropdown = !this.actionDropdown;
    };

    @action public handleCheckOne = async (id: number): Promise<void> => {
        this.accountId = id;

        try {
            await this.sdkStaff.postAffordabilityCheck({
                requestBody: {
                    checkList: [id],
                },
            });
        } catch (error) {
            console.error(error);
        }

        await this.data.get(this.affordabilityStatus).get(this.page).refresh();
    };

    @action public handleCheckAll = async (): Promise<void> => {
        if (this.dangerCheckbox === true) {
            const accountIdAll: Array<number> = [];

            this.setAlertBox();
            this.dangerCheckbox = false;

            this.totalItems?.data.map((item) => accountIdAll.push(item.accountId));

            try {
                await this.sdkStaff.postAffordabilityCheck({
                    requestBody: {
                        checkList: accountIdAll,
                    },
                });
            } catch (error) {
                console.error(error);
            }

            await this.data.get(this.affordabilityStatus).get(this.page).refresh();
        }
    };
}
